import {
    AfterContentInit,
    ChangeDetectorRef,
    Component, ContentChildren, EventEmitter, Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChildren,
    ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Objects } from 'src/modules/utils/shared/objects';
import { Utils } from "src/modules/utils/shared/utils";
import { SmTabComponent, SmTabContentLoadMode } from '../sm-tab/sm-tab.component';
import { TabPanel } from '@openng/optimus-ui/tabs';


@Component({
    selector: 'sm-tab-view',
    standalone: false,
    templateUrl: './sm-tab-view.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./sm-tab-view.component.scss']
})
export class SmTabViewComponent implements AfterContentInit, OnDestroy {
    private _value: string;
    private _pauseInactiveTabChangeDetection = false;
    private pausedTabValues = new Set<string>();
    private pauseTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
    private loadedTabValues = new Set<string>();
    private renderedTabValues = new Set<string>();
    private preparingTabValues = new Set<string>();
    private prepareAnimationFrames = new Map<string, number[]>();
    private fallbackSelectionTimeout?: ReturnType<typeof setTimeout>;

    @Input()
    get value(): string {
        return this._value;
    }
    set value(value: string) {
        const normalizedValue = value == null ? null : Objects.toString(value);
        if (this._value === normalizedValue) {
            return;
        }

        const previousValue = this._value;
        this.cancelFallbackSelection();
        this._value = normalizedValue;
        this.markTabAsLoaded(normalizedValue);
        this.cancelPreparationIfContentIsDestroyed(previousValue);
        this.prepareTabForDisplay(normalizedValue);
        this.resumeChangeDetection(normalizedValue);
        this.scheduleChangeDetectionPause(previousValue);
    }

    @Input()
    get pauseInactiveTabChangeDetection(): boolean {
        return this._pauseInactiveTabChangeDetection;
    }
    set pauseInactiveTabChangeDetection(value: boolean) {
        if (this._pauseInactiveTabChangeDetection === value) {
            return;
        }

        this._pauseInactiveTabChangeDetection = value;
        if (value) {
            this.initializePausedTabs();
        }
        else {
            this.clearPauseTimeouts();
            this.pausedTabValues.clear();
        }
        this.cdr.markForCheck();
    }

    @Input()
    inactiveTabChangeDetectionDelay = 1000;

    @Input()
    contentLoadMode: SmTabContentLoadMode = "eager";

    @Input()
    hideTabListIfSingle = true;
    @Output() valueChange = new EventEmitter<string>();
    @Output() onCloseTab = new EventEmitter<string>();

    @ContentChildren(SmTabComponent, {descendants: false}) tabChildren!: QueryList<SmTabComponent>;

    @ViewChildren(TabPanel) tabPanels!: QueryList<TabPanel>;

    tabs: SmTabComponent[] = [];
    private sub?: Subscription;

    constructor(private cdr: ChangeDetectorRef) {}

    ngAfterContentInit(): void {
        this.rebuildTabs();

        this.sub = this.tabChildren.changes.subscribe(() => {
            this.rebuildTabs();
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
        this.cancelFallbackSelection();
        this.clearPauseTimeouts();
        this.clearPrepareAnimationFrames();
    }

    private rebuildTabs(): void {
        this.cancelFallbackSelection();
        this.tabs = this.tabChildren?.toArray() ?? [];

        if (this.pauseInactiveTabChangeDetection && this.tabs.some(tab => !tab.hasDedicatedContentTemplate())) {
            throw new Error("Tabs mit pausierter Change Detection müssen ihren Inhalt mit *smTabContent kennzeichnen");
        }

        if (this.tabs.some(tab => this.getContentLoadMode(tab) != "eager" && !tab.hasDedicatedContentTemplate())) {
            throw new Error("Tabs mit verzögertem oder erneutem Laden müssen ihren Inhalt mit *smTabContent kennzeichnen");
        }

        this.markTabAsLoaded(this.value);
        this.initializePausedTabs();

        if (this.tabs.length == 0) {
            // Dynamisch projizierte Tabs können während eines Neuaufbaus vorübergehend fehlen.
            // Die Auswahl muss erhalten bleiben, damit das bisherige Panel danach wieder aktiv wird.
            return;
        }

        const exists = this.tabs.some((t, i) => this.getTabValue(t, i) === this.value);
        if (!exists) {
            // Die übergeordnete Auswahl erst nach der laufenden Angular-Prüfung ändern.
            const previousValue = this.value;
            this.fallbackSelectionTimeout = Utils.setTimerOnce(0, () => {
                this.fallbackSelectionTimeout = null;
                if (this.value !== previousValue || this.tabs.length == 0 ||
                    this.tabs.some((tab, index) => this.getTabValue(tab, index) === this.value)) {
                    return;
                }

                this.value = this.getTabValue(this.tabs[0], 0);
                this.cdr.markForCheck();
                this.valueChange.emit(this.value);
            });
        }
        else {
            this.prepareTabForDisplay(this.value);
        }
    }

    private cancelFallbackSelection(): void {
        if (this.fallbackSelectionTimeout != null) {
            clearTimeout(this.fallbackSelectionTimeout);
            this.fallbackSelectionTimeout = null;
        }
    }

    onChange(v: string | number): void {
        this.value = Objects.toString(v);
        this.valueChange.emit(this.value);
    }

    close(tabValue: string, ev: MouseEvent): void {
        ev.stopPropagation();
        this.onCloseTab.emit(tabValue);
    }

    getTabValue(tab: SmTabComponent, index: number): string {
        return Objects.toString(tab.value ?? index);
    }

    getTabTrack(tab: SmTabComponent, index: number): string {
        return this.getTabValue(tab, index);
    }

    getContentLoadMode(tab: SmTabComponent): SmTabContentLoadMode {
        return tab.contentLoadMode ?? this.contentLoadMode;
    }

    shouldRenderTab(tab: SmTabComponent, index: number): boolean {
        const tabValue = this.getTabValue(tab, index);
        switch (this.getContentLoadMode(tab)) {
            case "lazy":
                return this.loadedTabValues.has(tabValue);
            case "destroyOnHide":
                return tabValue === this.value;
            default:
                return true;
        }
    }

    isTabPreparing(tab: SmTabComponent, index: number): boolean {
        const tabValue = this.getTabValue(tab, index);
        return tabValue === this.value && this.preparingTabValues.has(tabValue);
    }

    isTabChangeDetectionPaused(tab: SmTabComponent, index: number): boolean {
        const tabValue = this.getTabValue(tab, index);
        return this.pauseInactiveTabChangeDetection && !tab.keepChangeDetectionActive && this.pausedTabValues.has(tabValue);
    }

    private initializePausedTabs(): void {
        if (!this.pauseInactiveTabChangeDetection || this.tabs.length == 0) {
            return;
        }

        for (let i = 0; i < this.tabs.length; i++) {
            const tabValue = this.getTabValue(this.tabs[i], i);
            if (tabValue === this.value) {
                this.resumeChangeDetection(tabValue);
            }
            else if (!this.pauseTimeouts.has(tabValue)) {
                this.pausedTabValues.add(tabValue);
            }
        }
    }

    private scheduleChangeDetectionPause(tabValue: string): void {
        if (!this.pauseInactiveTabChangeDetection || tabValue == null) {
            return;
        }

        this.clearPauseTimeout(tabValue);
        const timeout = Utils.setTimerOnce(this.inactiveTabChangeDetectionDelay, () => {
            this.pauseTimeouts.delete(tabValue);
            if (tabValue !== this.value) {
                this.pausedTabValues.add(tabValue);
                this.cdr.detectChanges();
            }
        }) as ReturnType<typeof setTimeout>;
        this.pauseTimeouts.set(tabValue, timeout);
    }

    private resumeChangeDetection(tabValue: string): void {
        if (tabValue == null) {
            return;
        }

        this.clearPauseTimeout(tabValue);
        this.pausedTabValues.delete(tabValue);
    }

    private clearPauseTimeout(tabValue: string): void {
        const timeout = this.pauseTimeouts.get(tabValue);
        if (timeout != null) {
            clearTimeout(timeout);
            this.pauseTimeouts.delete(tabValue);
        }
    }

    private clearPauseTimeouts(): void {
        for (const timeout of this.pauseTimeouts.values()) {
            clearTimeout(timeout);
        }
        this.pauseTimeouts.clear();
    }

    private markTabAsLoaded(tabValue: string): void {
        if (tabValue != null) {
            this.loadedTabValues.add(tabValue);
        }
    }

    private prepareTabForDisplay(tabValue: string): void {
        if (tabValue == null || this.tabs.length == 0) {
            return;
        }

        const tabIndex = this.tabs.findIndex((tab, index) => this.getTabValue(tab, index) === tabValue);
        if (tabIndex < 0) {
            return;
        }

        const loadMode = this.getContentLoadMode(this.tabs[tabIndex]);
        if (loadMode === "eager" ||
            loadMode === "lazy" && this.renderedTabValues.has(tabValue)) {
            this.preparingTabValues.delete(tabValue);
            return;
        }

        this.cancelPrepareAnimationFrames(tabValue);
        this.preparingTabValues.add(tabValue);

        const animationFrames: number[] = [];
        this.prepareAnimationFrames.set(tabValue, animationFrames);
        animationFrames.push(requestAnimationFrame(() => {
            animationFrames.push(requestAnimationFrame(() => {
                this.prepareAnimationFrames.delete(tabValue);
                this.preparingTabValues.delete(tabValue);
                if (loadMode === "lazy") {
                    this.renderedTabValues.add(tabValue);
                }
                this.cdr.detectChanges();
            }));
        }));
    }

    private cancelPreparationIfContentIsDestroyed(tabValue: string): void {
        if (tabValue == null || this.tabs.length == 0) {
            return;
        }

        const tabIndex = this.tabs.findIndex((tab, index) => this.getTabValue(tab, index) === tabValue);
        if (tabIndex >= 0 && this.getContentLoadMode(this.tabs[tabIndex]) === "destroyOnHide") {
            this.cancelPrepareAnimationFrames(tabValue);
            this.preparingTabValues.delete(tabValue);
        }
    }

    private cancelPrepareAnimationFrames(tabValue: string): void {
        const animationFrames = this.prepareAnimationFrames.get(tabValue);
        if (animationFrames != null) {
            for (const animationFrame of animationFrames) {
                cancelAnimationFrame(animationFrame);
            }
            this.prepareAnimationFrames.delete(tabValue);
        }
    }

    private clearPrepareAnimationFrames(): void {
        for (const tabValue of this.prepareAnimationFrames.keys()) {
            this.cancelPrepareAnimationFrames(tabValue);
        }
    }

    navigateToElement(el: HTMLElement): void {
        for(const panel of this.tabPanels.toArray()) {
            if(panel.el.nativeElement.contains(el)) {
                this.onChange(panel.value());
                return;
            }
        }
    }
}
